import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category/movie" },
  { label: "电视剧", to: "/category/tv" },
  { label: "关于我们", to: "/about" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  const isHome = location.pathname === "/";
  const solid = scrolled || !isHome || menuOpen;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        solid
          ? "bg-background-50/95 backdrop-blur border-b border-background-200/70"
          : "bg-gradient-to-b from-background-50/80 to-transparent border-b border-transparent"
      }`}
    >
      <div className="flex items-center justify-between h-16 md:h-20 px-4 md:px-8">
        <Link to="/" className="flex items-center gap-2 shrink-0 cursor-pointer">
          <span className="w-9 h-9 rounded-md bg-accent-500 flex items-center justify-center text-background-50">
            <i className="ri-play-fill text-2xl leading-none"></i>
          </span>
          <span className="font-heading text-xl md:text-2xl font-extrabold tracking-wide text-foreground-950">
            91<span className="text-accent-500">在线看</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 lg:gap-2">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `px-4 py-2 rounded-md text-sm lg:text-base font-label transition-colors cursor-pointer ${
                  isActive
                    ? "text-foreground-950 bg-background-200/70"
                    : "text-foreground-600 hover:text-foreground-950 hover:bg-background-200/50"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <form onSubmit={handleSearch} className="hidden md:flex items-center">
            <div className="flex items-center bg-background-100 border border-background-200 rounded-full pl-3 pr-1 h-10 focus-within:border-primary-500 transition-colors">
              <span className="w-5 h-5 flex items-center justify-center text-foreground-500">
                <i className="ri-search-line text-base"></i>
              </span>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                type="text"
                name="q"
                placeholder="搜索影视名称、演员…"
                className="w-40 lg:w-56 bg-transparent text-sm text-foreground-950 placeholder:text-foreground-500 px-2 outline-none"
              />
              <button
                type="submit"
                className="h-8 w-8 shrink-0 rounded-full bg-primary-500 hover:bg-primary-600 text-foreground-950 flex items-center justify-center cursor-pointer transition-colors"
                aria-label="搜索"
              >
                <i className="ri-arrow-right-line text-base"></i>
              </button>
            </div>
          </form>

          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-md border border-background-200 text-foreground-950 cursor-pointer"
            aria-label="打开菜单"
          >
            <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-2xl`}></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200/70 bg-background-50/95 backdrop-blur animate-fade-in">
          <div className="px-4 py-4 space-y-3">
            <form onSubmit={handleSearch} className="flex items-center bg-background-100 border border-background-200 rounded-full pl-3 pr-1 h-11">
              <span className="w-5 h-5 flex items-center justify-center text-foreground-500">
                <i className="ri-search-line text-base"></i>
              </span>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                type="text"
                name="q"
                placeholder="搜索影视名称、演员…"
                className="flex-1 bg-transparent text-sm text-foreground-950 placeholder:text-foreground-500 px-2 outline-none"
              />
              <button
                type="submit"
                className="h-9 w-9 shrink-0 rounded-full bg-primary-500 text-foreground-950 flex items-center justify-center cursor-pointer"
                aria-label="搜索"
              >
                <i className="ri-arrow-right-line text-lg"></i>
              </button>
            </form>
            <div className="flex flex-col">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-3 rounded-md text-base font-label cursor-pointer ${
                      isActive
                        ? "text-foreground-950 bg-background-200/70"
                        : "text-foreground-700 hover:bg-background-200/50"
                    }`
                  }
                >
                  <i className="ri-arrow-right-s-line text-lg"></i>
                  {item.label}
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}